import React, { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './auth/AuthContext';
import LoginView from './views/LoginView';
import LoginCallback from './auth/LoginCallback';
import VerificationDetailView from './views/VerificationDetailView';
import BusinessesView from './views/Businesses';
import OwnersView from './views/OwnersView';
import ProfileView from './ProfileView';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      refetchOnWindowFocus: false,
    },
  },
});

// Create theme (same as original)
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#eff3f7',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", sans-serif',
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0 4px 6px rgba(0, 0, 0, 0.07)',
    ...Array(22).fill(''),
  ],
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.07)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
        },
      },
    },
  },
});

// Loading component
const AppLoading = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

// App routes component
const AppRoutes = () => {
  const { isLoading, isAuthenticated, error, isBootstrapping } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to verifications after successful authentication and bootstrap completion
  useEffect(() => {
    if (!isLoading && !isBootstrapping && isAuthenticated) {
      // Only redirect if we're on the root path, login path, or callback path
      // This prevents disrupting navigation within the app
      const shouldRedirect =
        location.pathname === '/' ||
        location.pathname === '/login' ||
        location.pathname.startsWith('/login/callback');

      if (shouldRedirect) {
        console.log(
          'Authentication and bootstrap complete, redirecting to /verifications'
        );
        navigate('/verifications', { replace: true });
      }
    }
  }, [
    isLoading,
    isAuthenticated,
    isBootstrapping,
    navigate,
    location.pathname,
  ]);

  if (isLoading || (isAuthenticated && isBootstrapping)) {
    return <AppLoading />;
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <div>Error: {error}</div>
      </Box>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login/callback" element={<LoginCallback />} />
        <Route path="*" element={<LoginView />} />
      </Routes>
    );
  }

  // Show protected routes if authenticated
  return (
    <Routes>
      <Route path="/" element={<BusinessesView />} />
      <Route path="/businesses" element={<BusinessesView />} />
      <Route path="/verifications" element={<BusinessesView />} />
      <Route path="/verifications/:id" element={<VerificationDetailView />} />
      <Route path="/owners" element={<OwnersView />} />
      <Route path="/settings" element={<ProfileView />} />
      <Route path="/profile" element={<ProfileView />} />

      {/* Redirect login and callback to main app if already authenticated */}
      <Route path="/login" element={<Navigate to="/verifications" replace />} />
      <Route
        path="/login/callback"
        element={<Navigate to="/verifications" replace />}
      />

      {/* Catch-all route */}
      <Route path="*" element={<BusinessesView />} />
    </Routes>
  );
};

// Main App component
const App = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <SnackbarProvider
              maxSnack={3}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <AppRoutes />
            </SnackbarProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
